import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { LoadingSpinner } from '@/components/ui'
import { Search, Database, TrendingUp, Globe, DollarSign, BarChart3 } from 'lucide-react'

interface Instrument {
  id: number
  name: string
  short_name: string | null
  asset_type: string
  currency: string | null
  country: string | null
  exchange: string | null
  is_active: boolean
  ticker_count: number
  price_count: number
  last_price_date: string | null
}

interface InstrumentsResponse {
  instruments: Instrument[]
  total: number
  limit: number
  offset: number
}

const assetTypeConfig: Record<string, { label: string; color: string; icon: typeof Globe }> = {
  index: { label: 'Index', color: 'bg-blue-100 text-blue-700', icon: BarChart3 },
  etf: { label: 'ETF', color: 'bg-purple-100 text-purple-700', icon: TrendingUp },
  equity: { label: 'Equity', color: 'bg-green-100 text-green-700', icon: TrendingUp },
  fx: { label: 'FX', color: 'bg-amber-100 text-amber-700', icon: DollarSign },
  commodity: { label: 'Commodity', color: 'bg-orange-100 text-orange-700', icon: Globe },
  rate: { label: 'Rate', color: 'bg-cyan-100 text-cyan-700', icon: BarChart3 },
  crypto: { label: 'Crypto', color: 'bg-pink-100 text-pink-700', icon: DollarSign },
  bond: { label: 'Bond', color: 'bg-gray-100 text-gray-700', icon: Database },
  fund: { label: 'Fund', color: 'bg-teal-100 text-teal-700', icon: Database },
  future: { label: 'Future', color: 'bg-red-100 text-red-700', icon: TrendingUp },
}

const assetTypes = ['', 'index', 'etf', 'equity', 'fx', 'commodity', 'rate', 'crypto', 'bond', 'fund', 'future']

export default function Instruments() {
  const [search, setSearch] = useState('')
  const [assetFilter, setAssetFilter] = useState('')
  const [page, setPage] = useState(0)
  const limit = 50

  const { data, isLoading } = useQuery<InstrumentsResponse>({
    queryKey: ['instruments', search, assetFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      if (assetFilter) params.set('asset_type', assetFilter)
      params.set('limit', String(limit))
      params.set('offset', String(page * limit))
      const res = await api.get(`/instruments/instruments?${params}`)
      return res.data
    },
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Instruments</h1>
        <p className="text-muted-foreground mt-1">
          Plutus market data universe — {data?.total ?? 0} instruments
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search instruments..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={assetFilter}
          onChange={(e) => { setAssetFilter(e.target.value); setPage(0) }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">All Types</option>
          {assetTypes.filter(Boolean).map((t) => (
            <option key={t} value={t}>{assetTypeConfig[t]?.label ?? t}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Short</th>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-left px-4 py-3 font-medium">CCY</th>
                <th className="text-left px-4 py-3 font-medium">Country</th>
                <th className="text-right px-4 py-3 font-medium">Tickers</th>
                <th className="text-right px-4 py-3 font-medium">Prices</th>
                <th className="text-left px-4 py-3 font-medium">Last Date</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data?.instruments.map((inst) => {
                const cfg = assetTypeConfig[inst.asset_type] ?? { label: inst.asset_type, color: 'bg-gray-100 text-gray-600' }
                return (
                  <tr key={inst.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{inst.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{inst.short_name ?? '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">{inst.currency ?? '-'}</td>
                    <td className="px-4 py-3">{inst.country ?? '-'}</td>
                    <td className="px-4 py-3 text-right">{inst.ticker_count}</td>
                    <td className="px-4 py-3 text-right">{inst.price_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-muted-foreground">{inst.last_price_date ?? 'No data'}</td>
                  </tr>
                )
              })}
              {data?.instruments.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No instruments found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            Showing {page * limit + 1}-{Math.min((page + 1) * limit, data.total)} of {data.total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 rounded border text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={(page + 1) * limit >= data.total}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 rounded border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
